import { DateTime } from "luxon";
import { cloneDeep } from "lodash-es";

import LocalForagePlugin, { industrySectors, syncState } from "@/plugins/LocalForagePlugin";
import { industrySectorsSyncKey } from "@/types/products/product-definitions";

import { goFetch } from "@/utils/network-utils";
import type { PersistedQueryPayload } from "@/types/api/fetch";

import type { IndustrySector } from "@/types/entities";
import type { IndustrySectorApiResponse, IndustrySectors } from "@/types/api/content-models";

import { consoleLoggerFactory, getDefaultLoggerOptions, LogLevels } from "@/types/console-logger";
const logger = consoleLoggerFactory.create( getDefaultLoggerOptions( LogLevels.ERROR ) );

export async function refreshIndustrySectorsFromServer(): Promise< IndustrySector[] > {
    let allIndustrySectors: IndustrySector[] = [];

    const response =
        await goFetch< IndustrySectors, PersistedQueryPayload >( {
            endpoint: 'content/graphql',
            payload: {
                id: 'cfd9c68bb520360820fcbb3987f963ce',
                variables: { skip: 0, limit: 100 }
            }
        } );

    if ( null != response?.data?.industrySectorCatalog?.industry_sectors ) {
        allIndustrySectors = [ ...( response.data.industrySectorCatalog.industry_sectors
                // Perform a map to remove the extraneously added meta data from Apollo
                .map( ( is: IndustrySectorApiResponse ): IndustrySector => ( {
                    id: is.id,
                    name: is.name,
                    slug: is.slug,
                    symbol: is.short_name,
                    sectorIds: [ ...( is.assoc_sector_ids ? is.assoc_sector_ids : [] ) ]
                } ) )
        ) ];
    }

    return allIndustrySectors;
}

export async function loadIndustrySectorMap( industrySectorsMap: Map< string, IndustrySector > ): Promise< boolean > {
    let updated = false;
    try {
        const t0: number = performance.now();

        if ( typeof industrySectors === 'undefined' || typeof syncState === 'undefined' ) {
            // Wait for the driver to settle
            LocalForagePlugin.initialize();
        }

        await syncState.ready();
        // Check for the next refresh interval
        const syncThresholdInMillis = await syncState.getItem< number >( industrySectorsSyncKey );
        if ( null == syncThresholdInMillis || syncThresholdInMillis < DateTime.utc().toMillis() ) {
            updated = true;

            // Retrieve the latest collection of industry sectors from the content service
            const industrySectorsReceived: IndustrySector[] = await refreshIndustrySectorsFromServer();

            // Store the items recently retrieved
            await industrySectors.ready();
            for ( const industrySector of industrySectorsReceived ) {
                await industrySectors.setItem( industrySector.id, cloneDeep( industrySector ) );
            }

            const nextSyncThresholdInMillis = DateTime.utc().plus( { days: 30 } ).toMillis();
            await syncState.setItem( industrySectorsSyncKey, nextSyncThresholdInMillis );

            const t1: number = performance.now();
            logger.info( `[ sync-worker ] Refreshed ${ industrySectorsMap.size } industry sectors from server in ${ t1 - t0 } ms` );

        }

        industrySectorsMap.clear();

        // Load whatever is in the local cache
        await industrySectors.ready();
        // Iterate over all elements within IDB
        await industrySectors.iterate( ( industrySector: IndustrySector ) => {
            industrySectorsMap.set( industrySector.id, industrySector );
            return;
        } );
    } catch ( err: any ) {
        logger.error( JSON.stringify( err.message ) );
    }

    return updated;
}
